import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import SEO from '../components/SEO';
export default function BlogReliableAPIs() {
  useReveal();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  return (
    <div className="page">
      <SEO
  title="Building Reliable APIs: What Most Systems Get Wrong | Arhatinfo"
  description="Most API failures come from inconsistent standards, poor error handling, and no versioning. Learn how to design APIs that are stable, secure, and maintainable for years."
  keywords="reliable API design, REST API best practices, API versioning, API error handling, API security, rate limiting, API observability, production API, Django REST API"
  canonical="https://arhatinfo.com/blog/reliable-apis"
  og={{
    title:       "Building Reliable APIs: What Most Systems Get Wrong",
    description: "Reliable APIs are not accidental — they are engineered. Learn the 6 most common API mistakes and how to fix them.",
    url:         "https://arhatinfo.com/blog/reliable-apis",
    image:       "https://arhatinfo.com/og-blog.png",
  }}
/>
      <article>

        {/* ── HERO ─────────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg,var(--bg2),var(--bg3))',
          borderBottom: '1px solid var(--border-s)',
          padding: '80px 5vw 60px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position:'absolute', inset:0,
            backgroundImage:'linear-gradient(rgba(53,187,234,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(53,187,234,.04) 1px,transparent 1px)',
            backgroundSize:'50px 50px', pointerEvents:'none',
          }}/>
          <div className="wrap" style={{ position:'relative', zIndex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'.5rem', marginBottom:'1.5rem', fontSize:'.8rem', fontWeight:500 }}>
              <Link to="/"     style={{ color:'var(--muted)', textDecoration:'none' }}>Home</Link>
              <span style={{ color:'var(--muted)' }}>›</span>
              <Link to="/blog" style={{ color:'var(--muted)', textDecoration:'none' }}>Blog</Link>
              <span style={{ color:'var(--muted)' }}>›</span>
              <span style={{ color:'var(--blue-hi)' }}>API Design</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'.75rem', marginBottom:'1.2rem', flexWrap:'wrap' }}>
              <span style={{
                fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase',
                color:'var(--blue-hi)', background:'rgba(53,187,234,.1)',
                border:'1px solid rgba(53,187,234,.25)', padding:'.28rem .9rem', borderRadius:20,
              }}>API Design</span>
              <span style={{ fontSize:'.78rem', fontWeight:500, color:'var(--muted)' }}>10 min read</span>
              <span style={{ color:'var(--muted)', fontSize:'.78rem' }}>·</span>
              <span style={{ fontSize:'.78rem', fontWeight:500, color:'var(--muted)' }}>May 2025</span>
            </div>
            <h1 style={{
              fontFamily:'var(--font)', fontSize:'clamp(1.9rem,4.5vw,3.2rem)',
              fontWeight:900, color:'var(--white)', lineHeight:1.1, letterSpacing:'-.02em',
              maxWidth:820, marginBottom:'1.4rem', textShadow:'0 4px 20px rgba(0,0,0,.25)',
            }}>
              Building Reliable APIs:{' '}
              <span style={{ color:'var(--blue-hi)' }}>What Most Systems Get Wrong</span>
            </h1>
            <div style={{ display:'flex', alignItems:'center', gap:'.75rem' }}>
              <div style={{
                width:40, height:40, borderRadius:'50%', flexShrink:0,
                background:'linear-gradient(135deg,var(--blue-dark),var(--blue-deep))',
                border:'2px solid var(--blue-hi)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'.78rem', fontWeight:700, color:'var(--white)',
              }}>RJ</div>
              <div>
                <div style={{ fontSize:'.85rem', fontWeight:700, color:'var(--white)' }}>Rohit Jain</div>
                <div style={{ fontSize:'.75rem', fontWeight:500, color:'var(--muted)' }}>arhatinfo.com · Backend & AI Engineering</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BODY ─────────────────────────────────────────────────── */}
        <div style={{ padding:'60px 5vw 80px' }}>
          <div style={{ maxWidth:780, margin:'0 auto' }}>

            <Lead>
              Modern applications depend on APIs for everything — mobile apps, SaaS platforms,
              AI integrations, payment systems, dashboards, and automation workflows. Yet many
              APIs are built only to "work today," not to survive scale, integrations, or
              long-term maintenance.
            </Lead>

            {/* impact grid */}
            <div className="rv" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', margin:'1.8rem 0' }}>
              <div style={{ background:'rgba(192,57,43,.07)', border:'.5px solid rgba(192,57,43,.3)', borderRadius:'var(--r)', padding:'1.3rem' }}>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'#E74C3C', marginBottom:'.9rem' }}>The result</div>
                {['Inconsistent response formats','Breaking changes without versioning','Weak authentication','Unclear error handling','APIs impossible to maintain'].map(f => (
                  <div key={f} style={{ display:'flex', gap:'.5rem', fontSize:'.83rem', fontWeight:500, color:'rgba(255,255,255,.7)', marginBottom:'.35rem' }}>
                    <span style={{ color:'#E74C3C' }}>✗</span>{f}
                  </div>
                ))}
              </div>
              <div style={{ background:'rgba(53,187,234,.06)', border:'.5px solid rgba(53,187,234,.3)', borderRadius:'var(--r)', padding:'1.3rem' }}>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--blue-hi)', marginBottom:'.9rem' }}>Reliable APIs increase</div>
                {['Scalability','Developer adoption','Long-term maintainability','Trust in your platform','Integration confidence'].map(f => (
                  <div key={f} style={{ display:'flex', gap:'.5rem', fontSize:'.83rem', fontWeight:500, color:'rgba(255,255,255,.7)', marginBottom:'.35rem' }}>
                    <span style={{ color:'var(--blue-hi)' }}>✓</span>{f}
                  </div>
                ))}
              </div>
            </div>

            <Callout>Reliable APIs are not just endpoints. They are contracts between systems.</Callout>

            {/* ── ARCHITECTURE PROBLEM ── */}
            <SectionHeading>The Architecture Problem Most Teams Ignore</SectionHeading>
            <Body>
              Many teams prioritize features over API consistency. They build endpoints quickly,
              and over time different developers return different formats, naming conventions, and
              status codes. This creates integration chaos.
            </Body>
            <CodeBlock lines={[
              '// Fragmented — what not to do',
              'GET  /users',
              'POST /user/create',
              'GET  /get-all-orders',
              'DELETE /removeUser?id=5',
            ]} label="fragmented_routes.http" bad />

            {/* ── MISTAKE 1 ── */}
            <SectionHeading>1. Inconsistent Response Structures</SectionHeading>
            <Body>
              One endpoint returns <code style={inlineCode}>{"{ \"status\": \"ok\" }"}</code>, another
              returns <code style={inlineCode}>{"{ \"payload\": {} }"}</code>, another
              returns <code style={inlineCode}>{"{ \"result\": {} }"}</code>. Frontend teams now
              need custom handling everywhere.
            </Body>

            {/* Diagram 1 */}
            <div className="rv" style={{ margin:'2rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="380" viewBox="0 0 900 380" xmlns="http://www.w3.org/2000/svg">
                <rect width="900" height="380" rx="20" fill="#0c0e14"/>
                <text x="450" y="44" textAnchor="middle" fontSize="20" fontFamily="Arial" fill="#eef2ff" fontWeight="700">API Response Design</text>
                {/* bad */}
                <rect x="40" y="68" width="370" height="252" rx="14" fill="#1a0e0e" stroke="#E74C3C" strokeWidth="1.5"/>
                <text x="225" y="102" textAnchor="middle" fontSize="16" fontFamily="Arial" fill="#E74C3C" fontWeight="700">❌  Inconsistent APIs</text>
                {[
                  '{ "status": "ok" }',
                  '{ "payload": { ... } }',
                  '{ "result": { ... } }',
                  '{ "data": null, "err": 1 }',
                ].map((line, i) => (
                  <text key={i} x="65" y={140 + i * 38} fontSize="14" fontFamily="Courier New" fill="#f87171">{line}</text>
                ))}
                <text x="65" y="295" fontSize="13" fontFamily="Arial" fill="#888">→ Frontend complexity grows with every endpoint</text>
                {/* good */}
                <rect x="490" y="68" width="370" height="252" rx="14" fill="#0a1a0e" stroke="#35BBEA" strokeWidth="1.5"/>
                <text x="675" y="102" textAnchor="middle" fontSize="16" fontFamily="Arial" fill="#35BBEA" fontWeight="700">✅  Standardized APIs</text>
                {[
                  '{',
                  '  "success": true,',
                  '  "message": "User fetched",',
                  '  "data": { ... },',
                  '  "errors": null',
                  '}',
                ].map((line, i) => (
                  <text key={i} x="515" y={138 + i * 28} fontSize="13" fontFamily="Courier New" fill="#35BBEA">{line}</text>
                ))}
                <text x="515" y="300" fontSize="13" fontFamily="Arial" fill="#888">→ Predictable — every integration works the same</text>
              </svg>
            </div>

            <SubHeading>Better Approach</SubHeading>
            <CodeBlock lines={[
              '{',
              '  "success": true,',
              '  "message": "User fetched successfully",',
              '  "data": { ... },',
              '  "errors": null',
              '}',
            ]} label="standard_response.json" />

            {/* ── MISTAKE 2 ── */}
            <SectionHeading>2. Poor Error Handling</SectionHeading>
            <Body>Many APIs return useless responses that help nobody. Reliable APIs provide clear error codes, human-readable messages, machine-readable identifiers, and traceability.</Body>

            <div className="rv" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', margin:'1.5rem 0' }}>
              <div>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'#E74C3C', marginBottom:'.6rem' }}>❌ Useless error</div>
                <CodeBlock lines={['{ "error": "Something went wrong" }']} label="" bad small />
              </div>
              <div>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--blue-hi)', marginBottom:'.6rem' }}>✅ Structured error</div>
                <CodeBlock lines={[
                  '{',
                  '  "success": false,',
                  '  "error": {',
                  '    "code": "USER_NOT_FOUND",',
                  '    "message": "User does not exist",',
                  '    "trace_id": "req_91823"',
                  '  }',
                  '}',
                ]} label="" small />
              </div>
            </div>

            {/* ── MISTAKE 3 ── */}
            <SectionHeading>3. No API Versioning</SectionHeading>
            <Body>
              One of the biggest long-term mistakes. Teams change response fields directly —
              renaming <code style={inlineCode}>"name"</code> to <code style={inlineCode}>"full_name"</code> — and
              suddenly mobile apps break, external clients fail, and integrations crash.
            </Body>

            {/* Diagram 2 */}
            <div className="rv" style={{ margin:'2rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="320" viewBox="0 0 900 320" xmlns="http://www.w3.org/2000/svg">
                <rect width="900" height="320" rx="20" fill="#0c0e14"/>
                <text x="450" y="44" textAnchor="middle" fontSize="20" fontFamily="Arial" fill="#eef2ff" fontWeight="700">Versioned API Architecture</text>
                {/* clients */}
                <rect x="40" y="90" width="170" height="56" rx="10" fill="#20303D" stroke="#35BBEA" strokeWidth="1.5"/>
                <text x="125" y="123" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">Mobile App v1</text>
                <rect x="40" y="190" width="170" height="56" rx="10" fill="#20303D" stroke="#35BBEA" strokeWidth="1.5"/>
                <text x="125" y="223" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">Partner System</text>
                <rect x="40" y="262" width="170" height="40" rx="10" fill="#20303D" stroke="#6A99AB" strokeWidth="1"/>
                <text x="125" y="287" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#6A99AB">New Client</text>
                {/* arrows */}
                <line x1="210" y1="118" x2="330" y2="105" stroke="#35BBEA" strokeWidth="1.5" strokeOpacity="0.5"/>
                <line x1="210" y1="218" x2="330" y2="210" stroke="#35BBEA" strokeWidth="1.5" strokeOpacity="0.5"/>
                <line x1="210" y1="282" x2="330" y2="282" stroke="#6A99AB" strokeWidth="1.5" strokeOpacity="0.4"/>
                {/* api versions */}
                <rect x="330" y="78" width="230" height="56" rx="10" fill="#111520" stroke="#35BBEA" strokeWidth="1.5"/>
                <text x="445" y="112" textAnchor="middle" fontSize="15" fontFamily="Courier New" fill="#35BBEA">/api/v1/users</text>
                <rect x="330" y="185" width="230" height="56" rx="10" fill="#111520" stroke="#4ade80" strokeWidth="1.5"/>
                <text x="445" y="218" textAnchor="middle" fontSize="15" fontFamily="Courier New" fill="#4ade80">/api/v2/users</text>
                <rect x="330" y="262" width="230" height="40" rx="10" fill="#111520" stroke="#6A99AB" strokeWidth="1"/>
                <text x="445" y="287" textAnchor="middle" fontSize="14" fontFamily="Courier New" fill="#6A99AB">/api/v3/users</text>
                {/* labels */}
                <text x="610" y="106" fontSize="14" fontFamily="Arial" fill="#6A99AB">← legacy clients still work</text>
                <text x="610" y="214" fontSize="14" fontFamily="Arial" fill="#4ade80">← new features added safely</text>
                <text x="610" y="282" fontSize="14" fontFamily="Arial" fill="#6A99AB">← future evolution</text>
              </svg>
            </div>

            <CodeBlock lines={[
              '# Versioned endpoints',
              'GET /api/v1/users      ← stable for existing clients',
              'GET /api/v2/users      ← new response structure',
              '',
              '# Or header-based versioning',
              'Accept: application/vnd.api+json; version=2',
            ]} label="versioning.http" />

            {/* ── MISTAKE 4 ── */}
            <SectionHeading>4. Weak Authentication & Authorization</SectionHeading>
            <Body>
              Some APIs still rely on static API keys, hardcoded secrets, or no role validation.
              This becomes dangerous at scale. Security must be designed from day one.
            </Body>
            <BulletList items={['OAuth2 for third-party access','JWT authentication with expiration','RBAC (Role-Based Access Control)','Refresh token rotation','Request signing where needed']} />
            <CodeBlock lines={[
              '# Every protected endpoint requires:',
              'Authorization: Bearer <JWT_TOKEN>',
              '',
              '# JWT payload includes:',
              '{ "user_id": "uuid", "role": "admin", "exp": 1234567890 }',
            ]} label="auth.http" />

            {/* ── MISTAKE 5 ── */}
            <SectionHeading>5. Ignoring Rate Limiting</SectionHeading>
            <Body>Without rate limiting, APIs get abused, bots overload servers, and costs explode. Rate limiting also communicates clearly to clients when they are being throttled.</Body>
            <CodeBlock lines={[
              '# Limit per authenticated user',
              '100 requests/minute per user',
              '1000 requests/hour per API key',
              '',
              '# Return informative headers',
              'X-RateLimit-Limit: 100',
              'X-RateLimit-Remaining: 20',
              'X-RateLimit-Reset: 1714000000',
              'Retry-After: 30',
            ]} label="rate_limit.http" />

            {/* ── MISTAKE 6 ── */}
            <SectionHeading>6. No Observability</SectionHeading>
            <Body>Many APIs fail silently. Without logging and monitoring, bugs become impossible to trace, performance bottlenecks remain hidden, and failures take hours to diagnose.</Body>

            {/* Diagram 3 — API lifecycle */}
            <div className="rv" style={{ margin:'2rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="280" viewBox="0 0 960 280" xmlns="http://www.w3.org/2000/svg">
                <rect width="960" height="280" rx="20" fill="#0c0e14"/>
                <text x="480" y="42" textAnchor="middle" fontSize="20" fontFamily="Arial" fill="#eef2ff" fontWeight="700">Reliable API Request Lifecycle</text>
                {[
                  { label:'Client',       x:30  },
                  { label:'API Gateway',  x:190 },
                  { label:'Auth',         x:375 },
                  { label:'Service',      x:555 },
                  { label:'Database',     x:740 },
                ].map(({ label, x }, i, arr) => (
                  <g key={i}>
                    <rect x={x} y={90} width={140} height={60} rx="10" fill="#111520" stroke="#35BBEA" strokeWidth="1.5"/>
                    <text x={x + 70} y={125} textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">{label}</text>
                    {i < arr.length - 1 && (
                      <line x1={x + 140} y1={120} x2={arr[i+1].x} y2={120} stroke="#35BBEA" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#arr3)"/>
                    )}
                  </g>
                ))}
                <defs>
                  <marker id="arr3" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L8,3 z" fill="#35BBEA" fillOpacity="0.5"/>
                  </marker>
                </defs>
                {/* observability bar */}
                <rect x="30" y="190" width="900" height="46" rx="10" fill="#20303D" stroke="#6A99AB" strokeWidth="1"/>
                <text x="480" y="219" textAnchor="middle" fontSize="15" fontFamily="Arial" fill="#6A99AB" fontWeight="700">
                  Logging  ·  Metrics  ·  Distributed Tracing  ·  Alerting  ·  Request IDs
                </text>
              </svg>
            </div>

            <CodeBlock lines={[
              '# Every response includes a trace ID',
              '{',
              '  "success": true,',
              '  "data": { ... },',
              '  "meta": {',
              '    "trace_id": "req_abc123",',
              '    "duration_ms": 42',
              '  }',
              '}',
            ]} label="observable_response.json" />

            {/* ── PRINCIPLES ── */}
            <SectionHeading>API Design Principles That Scale</SectionHeading>

            <SubHeading>Use REST Naming Standards</SubHeading>
            <div className="rv" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', margin:'1rem 0 1.5rem' }}>
              <div>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'#E74C3C', marginBottom:'.6rem' }}>❌ Bad</div>
                <CodeBlock lines={['/getUsers','/createUser','/deleteUserById','/fetchAllOrders']} label="" bad small />
              </div>
              <div>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--blue-hi)', marginBottom:'.6rem' }}>✅ Good</div>
                <CodeBlock lines={['GET    /users','POST   /users','GET    /users/123','DELETE /users/123']} label="" small />
              </div>
            </div>

            <SubHeading>Keep APIs Stateless</SubHeading>
            <Body>Avoid storing session state inside APIs. Stateless systems scale better, work well in distributed systems, and simplify deployments.</Body>

            <SubHeading>Validate Inputs Strictly</SubHeading>
            <Body>Never trust client input. Validate types, lengths, formats, and required fields. Use schema validation tools like Pydantic, Marshmallow, Joi, or Zod.</Body>

            <SubHeading>Document Everything</SubHeading>
            <Body>Undocumented APIs become internal nightmares. Use OpenAPI / Swagger, Postman collections, and examples for every endpoint. Good documentation reduces onboarding time massively.</Body>

            {/* ── STACK TABLE ── */}
            <SectionHeading>Production-Grade API Stack</SectionHeading>
            <div className="rv" style={{
              border:'.5px solid var(--border-hi)', borderRadius:'var(--r)',
              overflow:'hidden', margin:'1.5rem 0', boxShadow:'2px 10px 20px var(--shadow)',
            }}>
              {[
                ['Layer',         'Technology',            true],
                ['API Framework', 'Django REST / FastAPI', false],
                ['Auth',          'JWT + OAuth2',          true],
                ['Gateway',       'NGINX / Kong',          false],
                ['Database',      'PostgreSQL',            true],
                ['Caching',       'Redis',                 false],
                ['Async Tasks',   'Celery',                true],
                ['Monitoring',    'Grafana + Prometheus',  false],
                ['Logging',       'ELK Stack',             true],
                ['Docs',          'OpenAPI / Swagger',     false],
              ].map(([layer, tech, isHeader], i) => (
                <div key={i} style={{
                  display:'grid', gridTemplateColumns:'1fr 1fr',
                  background: isHeader && i === 0 ? 'var(--bg)' : i % 2 === 0 ? 'var(--bg2)' : 'var(--surface)',
                  borderBottom: i < 9 ? '1px solid var(--border-s)' : 'none',
                  padding:'.75rem 1.2rem',
                }}>
                  <div style={{ fontSize: i === 0 ? '.72rem' : '.88rem', fontWeight: i === 0 ? 700 : 500, color: i === 0 ? 'var(--muted)' : 'var(--white)', letterSpacing: i === 0 ? '.1em' : 0, textTransform: i === 0 ? 'uppercase' : 'none' }}>{layer}</div>
                  <div style={{ fontSize: i === 0 ? '.72rem' : '.88rem', fontWeight: i === 0 ? 700 : 500, color: i === 0 ? 'var(--muted)' : 'var(--blue-hi)', fontFamily: i === 0 ? 'var(--font)' : 'var(--mono)', letterSpacing: i === 0 ? '.1em' : 0, textTransform: i === 0 ? 'uppercase' : 'none' }}>{tech}</div>
                </div>
              ))}
            </div>

            {/* final callout */}
            <div className="rv" style={{
              margin:'3rem 0', padding:'2.5rem 2rem',
              background:'linear-gradient(135deg,var(--bg3),rgba(53,187,234,.06))',
              border:'.5px solid var(--border-hi)', borderRadius:'var(--r)',
              textAlign:'center', boxShadow:'2px 10px 30px var(--shadow)',
              position:'relative', overflow:'hidden',
            }}>
              <div style={{ position:'absolute', top:0, left:'20%', right:'20%', height:2, background:'var(--blue-hi)' }}/>
              <div style={{ fontFamily:'var(--font)', fontSize:'clamp(1rem,2.2vw,1.3rem)', fontWeight:900, color:'var(--white)', marginBottom:'1rem', lineHeight:1.4 }}>
                Most API failures are not caused by technology.
              </div>
              <div style={{ fontFamily:'var(--font)', fontSize:'clamp(.9rem,1.8vw,1.05rem)', fontWeight:500, color:'var(--white-70)', lineHeight:1.75, marginBottom:'1.2rem', maxWidth:560, margin:'0 auto 1.2rem' }}>
                They come from inconsistent standards, poor architecture decisions, and lack of long-term thinking.
                The difference between a prototype and a production platform is stability, predictability,
                security, and maintainability.
              </div>
              <div style={{ fontFamily:'var(--font)', fontSize:'1.05rem', fontWeight:700, color:'var(--blue-hi)' }}>
                Build APIs like infrastructure — not shortcuts.
              </div>
            </div>

            {/* key takeaways */}
            <SectionHeading>Key Takeaways</SectionHeading>
            <BulletList items={[
              'Standardize all API responses with a consistent envelope',
              'Implement structured error handling with codes and trace IDs',
              'Always version APIs — never make breaking changes silently',
              'Use strong JWT authentication with RBAC from day one',
              'Add rate limiting before you need it, not after',
              'Add observability from day one — logs, metrics, tracing',
              'Document everything — OpenAPI, examples, error codes',
              'Design APIs for long-term evolution, not just today',
            ]} />

            <Callout>Reliable APIs are not accidental. They are engineered.</Callout>

            {/* author bio */}
            <div className="rv" style={{
              display:'flex', gap:'1.2rem', alignItems:'flex-start',
              padding:'1.5rem', background:'var(--bg2)',
              border:'.5px solid var(--border-hi)', borderRadius:'var(--r)',
              boxShadow:'2px 10px 20px var(--shadow)', marginBottom:'3rem',
            }}>
              <div style={{
                width:52, height:52, borderRadius:'50%', flexShrink:0,
                background:'linear-gradient(135deg,var(--blue-dark),var(--blue-deep))',
                border:'2px solid var(--blue-hi)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'.88rem', fontWeight:700, color:'var(--white)',
              }}>RJ</div>
              <div>
                <div style={{ fontSize:'.92rem', fontWeight:700, color:'var(--white)', marginBottom:'.25rem' }}>Written by Rohit Jain</div>
                <div style={{ fontSize:'.8rem', fontWeight:500, color:'var(--muted)', marginBottom:'.6rem' }}>Founder, arhatinfo.com · Backend Engineer · AI Automation Specialist</div>
                <div style={{ fontSize:'.83rem', fontWeight:500, color:'var(--white-70)', lineHeight:1.7 }}>
                  Building scalable backend systems, automation platforms, cloud-native applications,
                  and AI-driven software solutions.
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="rv" style={{ textAlign:'center' }}>
              <div style={{ fontSize:'.82rem', fontWeight:500, color:'var(--muted)', marginBottom:'1rem' }}>
                Need a production-grade backend with reliable APIs?
              </div>
              <Link to="/contact" className="btn-p">Book a Free Consultation →</Link>
            </div>

          </div>
        </div>
      </article>
    </div>
  );
}

// ── INLINE CODE STYLE ─────────────────────────────────────────────────
const inlineCode = {
  fontFamily: 'var(--mono)',
  fontSize: '.85em',
  background: 'rgba(53,187,234,.1)',
  color: 'var(--blue-hi)',
  padding: '.1rem .4rem',
  borderRadius: 4,
};

// ── SHARED COMPONENTS ─────────────────────────────────────────────────

function Lead({ children }) {
  return <p style={{ fontSize:'1.05rem', fontWeight:500, color:'rgba(235,242,255,0.88)', lineHeight:1.8, marginBottom:'1.2rem' }}>{children}</p>;
}

function Body({ children }) {
  return <p style={{ fontSize:'.95rem', fontWeight:400, color:'rgba(235,242,255,0.78)', lineHeight:1.85, marginBottom:'1rem' }}>{children}</p>;
}

function SectionHeading({ children }) {
  return (
    <h2 style={{
      fontFamily:'var(--font)', fontSize:'clamp(1.3rem,2.5vw,1.75rem)',
      fontWeight:900, color:'var(--white)',
      marginTop:'2.8rem', marginBottom:'1rem', lineHeight:1.2,
      paddingBottom:'.6rem', borderBottom:'1px solid var(--border-s)',
    }}>{children}</h2>
  );
}

function SubHeading({ children }) {
  return (
    <h3 style={{
      fontFamily:'var(--font)', fontSize:'1.05rem', fontWeight:700,
      color:'var(--blue-hi)', marginTop:'1.8rem', marginBottom:'.6rem',
    }}>{children}</h3>
  );
}

function Callout({ children }) {
  return (
    <blockquote style={{
      margin:'1.8rem 0', padding:'1.1rem 1.4rem',
      background:'rgba(53,187,234,.06)', borderLeft:'3px solid var(--blue-hi)',
      borderRadius:'0 var(--r) var(--r) 0',
      fontSize:'1rem', fontWeight:600, color:'var(--white)', lineHeight:1.7, fontStyle:'italic',
    }}>{children}</blockquote>
  );
}

function BulletList({ items }) {
  return (
    <ul style={{ listStyle:'none', margin:'.8rem 0 1.2rem', padding:0, display:'flex', flexDirection:'column', gap:'.45rem' }}>
      {items.map((item, i) => (
        <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:'.65rem', fontSize:'.92rem', fontWeight:400, color:'rgba(235,242,255,0.78)', lineHeight:1.65 }}>
          <span style={{ color:'var(--blue-hi)', fontWeight:700, flexShrink:0, marginTop:2 }}>→</span>{item}
        </li>
      ))}
    </ul>
  );
}

function CodeBlock({ lines, label, bad = false, small = false }) {
  return (
    <div style={{
      background:'var(--bg2)', border:`.5px solid ${bad ? 'rgba(231,76,60,.35)' : 'var(--border-hi)'}`,
      borderRadius:'var(--r)', overflow:'hidden',
      margin: small ? '0' : '1.4rem 0',
      boxShadow: small ? 'none' : '2px 10px 20px var(--shadow)',
    }}>
      {label && (
        <div style={{ background:'rgba(255,255,255,.03)', borderBottom:'1px solid var(--border-s)', padding:'.55rem 1.1rem', display:'flex', alignItems:'center', gap:'.5rem' }}>
          {!small && <>
            <span style={{ width:10, height:10, borderRadius:'50%', background:'#ff5f57', display:'block' }}/>
            <span style={{ width:10, height:10, borderRadius:'50%', background:'#febc2e', display:'block' }}/>
            <span style={{ width:10, height:10, borderRadius:'50%', background:'#28c840', display:'block' }}/>
          </>}
          <span style={{ marginLeft:'auto', fontFamily:'var(--mono)', fontSize:'.65rem', color:'var(--muted)' }}>{label}</span>
        </div>
      )}
      <div style={{ padding: small ? '.75rem 1rem' : '1.2rem 1.4rem' }}>
        {lines.map((line, i) => (
          <div key={i} style={{
            fontFamily:'var(--mono)',
            fontSize: small ? '.73rem' : '.78rem',
            lineHeight:1.85,
            color: line.startsWith('#') ? 'rgba(106,153,171,.55)'
                 : line.startsWith('{') || line.startsWith('}') ? 'rgba(255,255,255,.4)'
                 : bad ? '#f87171' : '#35BBEA',
            whiteSpace:'pre',
          }}>{line || '\u00A0'}</div>
        ))}
      </div>
    </div>
  );
}